import React, { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { SavedPathway } from '../../../types/pathway';
import { SelectDropdown } from '../../SelectDropdown';
import { ResetFiltersButton } from '../../ResetFiltersButton';
import { PathwayButton } from '../PathwayButton';
import { SavedPathwayRow } from './SavedPathwayRow';
import { PathwayPagination } from '../PathwayPagination';
const PAGE_SIZE = 5;
interface SavedPathwaysViewProps {
  pathways: SavedPathway[];
  onOpen: (pathway: SavedPathway) => void;
  onDuplicate: (pathway: SavedPathway) => void;
  onBuildNew: () => void;
}
export function SavedPathwaysView({
  pathways,
  onOpen,
  onDuplicate,
  onBuildNew
}: SavedPathwaysViewProps) {
  const [orgFilter, setOrgFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const organizations = useMemo(() => Array.from(new Set(pathways.map(p => p.organization))).sort(), [pathways]);
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = pathways.filter(pathway => {
      if (orgFilter !== 'all' && pathway.organization !== orgFilter) return false;
      if (needle && !`${pathway.name} ${pathway.organization}`.toLowerCase().includes(needle)) {
        return false;
      }
      return true;
    });
    return filtered.slice().sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'company') return a.organization.localeCompare(b.organization) || a.name.localeCompare(b.name);
      if (sort === 'size') return b.moduleIds.length - a.moduleIds.length;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [pathways, orgFilter, query, sort]);
  const isFiltered = orgFilter !== 'all' || query.trim().length > 0 || sort !== 'recent';
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const resetFilters = () => {
    setOrgFilter('all');
    setQuery('');
    setSort('recent');
    setPage(1);
  };
  return <section aria-labelledby="saved-pathways-heading" data-id="element-1874" data-mp="1zsk2obhuoylg">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4" data-id="element-1875" data-mp="2wfono5joxw6e">
        <div data-id="element-1876" data-mp="2sfefqncc8vow">
          <h2 id="saved-pathways-heading" className="text-xl font-bold text-gray-900" data-id="element-1877" data-mp="2sewf7ein7vb6">
            Your pathways{pathways.length > 0 ? ` (${pathways.length})` : ''}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-gray-600" data-id="element-1878" data-mp="2iojvtpmke86m">
            Pick up something you started, or duplicate a pathway that worked
            for a new prospect.
          </p>
        </div>
        <PathwayButton onClick={onBuildNew} className="whitespace-nowrap px-8" data-id="element-1879" data-mp="3bd8nyz5n7368">
          <Plus size={18} data-id="element-1880" data-mp="3ipyhl267xf7y" />
          Build a New Pathway
        </PathwayButton>
      </div>

      {pathways.length > 0 && <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-1881" data-mp="3norvuwtbxa96">
          <label className="relative" data-id="element-1882" data-mp="3smtbf7rjqrfw">
            <span className="sr-only" data-id="element-1883" data-mp="3slrtqkg0z6p7">Search pathways</span>
            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1884" data-mp="jsih0nmukne" />
            <input value={query} onChange={e => {
          setQuery(e.target.value);
          setPage(1);
        }} placeholder="Search by name or company" className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#30718D]" data-id="element-1885" data-mp="5gsoxcoe9pf1" />
          </label>
          <SelectDropdown ariaLabel="Filter pathways by company" scrollMenu value={orgFilter} onChange={value => {
        setOrgFilter(value);
        setPage(1);
      }} options={[{
        value: 'all',
        label: 'All companies'
      }, ...organizations.map(org => ({
        value: org,
        label: org
      }))]} data-id="element-1886" data-mp="t9967l0b5dda" />
          <SelectDropdown ariaLabel="Sort pathways" value={sort} onChange={value => {
        setSort(value);
        setPage(1);
      }} options={[{
        value: 'recent',
        label: 'Recently updated'
      }, {
        value: 'name',
        label: 'Pathway name'
      }, {
        value: 'company',
        label: 'Company'
      }, {
        value: 'size',
        label: 'Most modules'
      }]} data-id="element-1887" data-mp="kcqs6je9fsf0" />
          <ResetFiltersButton isActive={isFiltered} onReset={resetFilters} data-id="element-1888" data-mp="1hywl821aiqyz" />
        </div>}

      {pathways.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1889" data-mp="1d0mqttne3uef">
          <h3 className="text-base font-bold text-gray-900" data-id="element-1890" data-mp="2ux6mr8pzzjox">
            No pathways yet
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-600" data-id="element-1891" data-mp="2zvhsb799f81c">
            Build one from a conversation you just had, or start from a proven
            pathway below.
          </p>
        </div> : visible.length === 0 ? <div className="rounded-xl border border-gray-100 bg-white px-6 py-10 text-center shadow-sm" data-id="element-1892" data-mp="3elyuistpy1za">
          <p className="text-sm text-gray-600" data-id="element-1893" data-mp="3iolph2los30p">
            No pathways match those filters.
          </p>
          <button type="button" onClick={resetFilters} className="mt-2 text-sm font-medium text-[#30718D] transition-colors hover:text-[#265a70]" data-id="element-1894" data-mp="34xgwukgqkazv">
            Clear filters
          </button>
        </div> : <div data-id="element-1895" data-mp="1e47l8owhu04">
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1896" data-mp="66f5k602z5q1">
          <div className="overflow-x-auto" data-id="element-1897" data-mp="g2dxcnyfp3yk">
            <table className="w-full table-fixed sm:min-w-[760px]" data-id="element-1898" data-mp="g1cnunx2gpiy">
              <caption className="sr-only" data-id="element-1899" data-mp="b31e0d6ruf0q">
                Saved pathways — expand a row to see its modules

              </caption>
              <colgroup data-id="element-1900" data-mp="62wsu9ux4w46">
                <col data-id="element-1901" data-mp="66kl75omnchb" />
                <col className="w-[200px]" data-id="element-1902" data-mp="15n6x46qn2l3" />
                <col className="w-[110px]" data-id="element-1903" data-mp="14lmi93u13qp" />
                <col className="w-[190px]" data-id="element-1904" data-mp="3t97bybdmf3jp" />
              </colgroup>
              <thead data-id="element-1905" data-mp="3taifca35m9aa">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-1906" data-mp="3tb0fviwun9o0">
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1907" data-mp="3ogw84xfrexlv">
                    Pathway
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-left text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1908" data-mp="3el2sh56qunzv">
                    Audience
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-center text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1909" data-mp="35jlfhvomv2fe">
                    Modules
                  </th>
                  <th className="whitespace-nowrap px-3 py-3 text-right text-sm font-semibold text-gray-700 sm:px-6" data-id="element-1910" data-mp="1l1aqhmggii5s">
                    <span className="sr-only" data-id="element-1911" data-mp="1p50di7hhgrop">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1912" data-mp="1z1vdyvxbh25m">
                {pageItems.map(pathway => <SavedPathwayRow key={pathway.id} pathway={pathway} expanded={expandedId === pathway.id} onToggleExpand={() => setExpandedId(expandedId === pathway.id ? null : pathway.id)} onOpen={onOpen} onDuplicate={onDuplicate} data-id="element-1913" data-mp="1yyze321ws3ej" />)}
              </tbody>
            </table>
          </div>
        </div>
        <PathwayPagination label="Your pathways pages" page={safePage} pageCount={pageCount} onChange={setPage} data-id="element-1914" data-mp="nl53v1prvq6q" />
        </div>}
    </section>;
}